const USER_TASK = "UserTask";
const SYSTEM_TASK = "Task";
const EXLUSIVE_GATEWAY = "ExclusiveGateway";
const PARALLEL_GATEWAY = "ParallelGateway";

export default class CustomContextPad {
  constructor(config, contextPad, create, elementFactory, injector, translate) {
    this.create = create;
    this.elementFactory = elementFactory;
    this.translate = translate;
    this.modeling = injector.get("modeling", null);
    this.selection = injector.get("selection", null);

    if (config.autoPlace !== false) {
      this.autoPlace = injector.get("autoPlace", false);
    }

    this.popupMenu = injector.get("popupMenu", null);

    contextPad.registerProvider(this);
  }

  getContextPadEntries = (element) => {
    const { autoPlace, create, elementFactory, translate, modeling, selection } = this;
    const pm = this.popupMenu;
    let customEntries = [];

    // The single outgoing sequence flow of a node, if it has exactly one (used to decide whether an
    // append should INSERT into the path or just fork a new one).
    function soleOutgoingFlow(el) {
      const flows = (el?.outgoing || []).filter(
        (c) => c?.businessObject?.$type === "bpmn:SequenceFlow" && Array.isArray(c.waypoints) && c.waypoints.length >= 2
      );
      return flows.length === 1 ? flows[0] : null;
    }

    // A point at 50% along a flow's polyline — where the inserted node docks. Must lie ON the line
    // so DropOnFlowBehavior's intersection test succeeds.
    function flowMidpoint(waypoints) {
      const segLens = [];
      let total = 0;
      for (let i = 1; i < waypoints.length; i++) {
        const dx = waypoints[i].x - waypoints[i - 1].x;
        const dy = waypoints[i].y - waypoints[i - 1].y;
        const len = Math.sqrt(dx * dx + dy * dy);
        segLens.push(len);
        total += len;
      }
      let remaining = total / 2;
      for (let i = 1; i < waypoints.length; i++) {
        if (remaining <= segLens[i - 1] || i === waypoints.length - 1) {
          const t = segLens[i - 1] ? remaining / segLens[i - 1] : 0;
          return {
            x: waypoints[i - 1].x + (waypoints[i].x - waypoints[i - 1].x) * t,
            y: waypoints[i - 1].y + (waypoints[i].y - waypoints[i - 1].y) * t
          };
        }
        remaining -= segLens[i - 1];
      }
      return waypoints[0];
    }

    // Drag handler: start a SOURCELESS interactive create so the new shape can be dropped onto an
    // existing sequence flow to INSERT it between two nodes (bpmn-js DropOnFlowBehavior). This is
    // the key detail — diagram-js Create only inserts on a flow when there is NO source: with a
    // source, `canCreate` resolves `connect=true` and Create calls `modeling.appendShape`, which
    // forks a brand-new outgoing flow + node instead of splitting the target flow. Sourceless
    // create routes through `modeling.createElements` → `shape.create` with parent=flow, which
    // DropOnFlowBehavior reconnects as source→new→target. (This mirrors the diagram palette, which
    // inserts for exactly the same reason.) Quick connected append stays on click via autoPlace.
    function appendDragStart(elType) {
      return function (event) {
        const shape = elementFactory.createShape({ type: `bpmn:${elType}` });
        create.start(event, shape, { hints: { autoSelect: [shape] } });
      };
    }

    // Click handler. If the node has exactly ONE outgoing sequence flow, INSERT the new step into
    // that flow (A → new → B) — i.e. "add an element into the path out from it, in between the two
    // nodes". Creating the shape with the flow as its parent routes through DropOnFlowBehavior,
    // which splits the flow and reconnects source → new → target. Otherwise (no outgoing = end of
    // chain, or several = a gateway fork) fall back to a quick auto-placed append.
    function appendItemInit(elType) {
      return function (event, element) {
        const shape = elementFactory.createShape({ type: `bpmn:${elType}` });
        // Gateways are branch points — appending from one must ALWAYS add a new outgoing path, never
        // split an existing one (even when it currently has a single outgoing). Only linear nodes
        // (task / start event / intermediate event) insert into their sole outgoing flow.
        const isGateway = element?.businessObject?.$instanceOf?.("bpmn:Gateway");
        const flow = isGateway ? null : soleOutgoingFlow(element);
        if (flow && modeling) {
          const inserted = modeling.createShape(shape, flowMidpoint(flow.waypoints), flow);
          // Focus the newly inserted node (autoPlace selects its result via AutoPlaceSelectionBehavior,
          // but a raw createShape does not — mirror that so append always lands on the new element).
          if (selection) {
            selection.select(inserted || shape);
          }
          return;
        }
        if (autoPlace) {
          autoPlace.append(element, shape);
        } else {
          create.start(event, shape, { hints: { autoSelect: [shape] } });
        }
      };
    }

    function removeUnwantedItems(entries, element) {
      const bo = element?.businessObject;

      // Happy Path phase columns (marked bpmn:Group): connect/append/replace make no sense on a
      // column — keep only delete. Rename is double-click; move/resize happen on the canvas.
      if (bo && bo.$type === "bpmn:Group" && bo.isHappyPathPhase === true) {
        for (const key of Object.keys(entries)) {
          if (key !== "delete") delete entries[key];
        }
        return;
      }

      // Text annotations are free-floating notes with no flow semantics — nothing can be appended
      // from them and they have no outgoing sequence flow, so the pad offers only delete.
      if (bo && bo.$type === "bpmn:TextAnnotation") {
        for (const key of Object.keys(entries)) {
          if (key !== "delete") delete entries[key];
        }
        return;
      }

      let unwanted = [
        "append.intermediate-event",
        "append.append-task",
        "append.gateway",
        "lane-divide-two",
        "lane-divide-three"
      ];

      if (bo && (bo.$type === "bpmn:SubProcess" || bo.$type === "bpmn:CallActivity")) {
        unwanted.push("replace");
      }

      for (const p of unwanted) {
        delete entries[p];
      }

      // VTO: This block is commented out as it is handled in customReplaceMenu.js
      // if (pm && !pm.__openHooked) {
      //   const origOpen = pm.open.bind(pm);

      //   const removeDomItems = () => {
      //     const popup = document.querySelector(".djs-popup");
      //     if (!popup) return;

      //     const ICON_CLASSES = [".bpmn-icon-subprocess-expanded", ".bpmn-icon-subprocess-collapsed"];
      //     ICON_CLASSES.forEach((cls) => {
      //       popup.querySelectorAll(cls).forEach((icon) => icon.closest(".entry")?.remove());
      //     });

      //     const LABELS = ["sub process (expanded)", "sub process (collapsed)"];

      //     popup.querySelectorAll(".entry").forEach((entry) => {
      //       const text = (entry.textContent || "").trim().toLowerCase();
      //       if (LABELS.includes(text)) entry.remove();
      //     });
      //   };

      //   pm.open = function (target, id, position, options) {
      //     const res = origOpen(target, id, position, options);
      //     if (id === "bpmn-replace") {
      //       if (typeof requestAnimationFrame === "function") {
      //         requestAnimationFrame(removeDomItems);
      //       } else {
      //         setTimeout(removeDomItems, 0);
      //       }
      //     }
      //     return res;
      //   };

      //   pm.__openHooked = true;
      // }
    }

    let taskEntries = {
      "append.task.service": {
        group: "model",
        className: "k-bpmn-icon-task",
        title: translate("Create Task"),
        action: {
          dragstart: appendDragStart(SYSTEM_TASK),
          click: appendItemInit(SYSTEM_TASK)
        }
      }
    };
    let gwEntries = {
      "append.gw.exclusive": {
        group: "model",
        className: "k-bpmn-icon-gateway-xor",
        title: translate("Create Exclusive Gateway"),
        action: {
          dragstart: appendDragStart(EXLUSIVE_GATEWAY),
          click: appendItemInit(EXLUSIVE_GATEWAY)
        }
      }
    };

    var businessObject = element.businessObject;
    if (
      element.type === "label" ||
      businessObject.$type === "bpmn:Lane" ||
      businessObject.$type === "bpmn:EndEvent" ||
      businessObject.$type === "bpmn:Participant" ||
      businessObject.$type === "bpmn:SequenceFlow" ||
      businessObject.$type === "bpmn:TextAnnotation" ||
      businessObject.$type === "bpmn:"
    ) {
      customEntries = {};
    } else {
      customEntries = { ...taskEntries, ...gwEntries };
    }

    return function (entries) {
      if (pm && entries.replace?.action?.click && !entries.replace.action.click._wrapped) {
        const origClick = entries.replace.action.click;
        const wrapped = function (...args) {
          return origClick.apply(this, args);
        };
        wrapped._wrapped = true;
        entries.replace.action.click = wrapped;
      }

      entries = { ...entries, ...customEntries };
      removeUnwantedItems(entries, element);
      return entries;
    };
  };
}

CustomContextPad.$inject = ["config", "contextPad", "create", "elementFactory", "injector", "translate"];
